import styled from 'styled-components';

import { simpleDateAtTime } from 'lib/date';
import type { RetroactiveDischargeRun } from 'services/api/admin/pccRetroactiveDischargeRuns';
import { colors } from 'styles/theme/colors';

import { liveSplit, previewSplit, RUN_STATUS_LABELS, runLabel } from './labels';

type Props = { runs: RetroactiveDischargeRun[]; totalRecords: number };

export default function RunHistory({ runs, totalRecords }: Props) {
  if (!runs.length) return <Muted>No runs yet.</Muted>;

  return (
    <div>
      <Table aria-label='Past retroactive discharge runs'>
        <thead>
          <tr>
            <Th>Run</Th>
            <Th>Started by</Th>
            <Th>Started</Th>
            <Th>Status</Th>
            <ThNum>Patients</ThNum>
            <Th>Result</Th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id} data-testid={`run-history-${run.id}`}>
              <Td>{runLabel(run)}</Td>
              <Td>{run.triggeredBy.name}</Td>
              <Td>{simpleDateAtTime(run.createdAt)}</Td>
              <Td>{RUN_STATUS_LABELS[run.status]}</Td>
              <TdNum>{run.counts.total}</TdNum>
              <Td>
                {run.mode === 'preview' ? previewSplit(run.counts) : liveSplit(run.counts)}
                {run.error && <ErrorText>{run.error}</ErrorText>}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {totalRecords > runs.length && (
        <Muted>
          Showing the {runs.length} most recent of {totalRecords} runs.
        </Muted>
      )}
    </div>
  );
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  font-weight: 500;
  padding: 8px;
  border-bottom: 1px solid ${colors.black10};
  font-size: 12px;
  color: ${colors.black75};
  white-space: nowrap;
`;

const ThNum = styled(Th)`
  text-align: right;
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid ${colors.black5};
  vertical-align: top;
`;

const TdNum = styled(Td)`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const Muted = styled.p`
  color: ${colors.black50};
  font-size: 14px;
  margin: 8px 0 0;
`;

const ErrorText = styled.div`
  color: ${colors.red100};
  font-size: 13px;
`;
